import React, { useState, Children, ReactNode, ReactElement } from 'react';
import {
  Box,
  Button,
  Stack,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { AbsoluteFill } from './AbsoluteFill';
import { ScrollPane } from './ScrollPane';

// TODO: Make these match the rest of the theme; for now these are copied from the previous design.
const PRIMARY_BLUE = 'rgb(0, 145, 234)';
const DISABLED_GREY = 'rgb(217, 224, 232)';

type StepState = 'pending' | 'active' | 'done';

type Props = {
  submitText?: string;
  onSubmit?: () => void;
  children: Array<ReactElement<StepperStepProps>>;
};

export function FormStepper(props: Props) {
  const { submitText, onSubmit, children } = props;
  const [tabIndex, setTabIndex] = useState(0);
  const steps = Children.toArray(children).filter(
    (child) => Object(child).type === StepperStep,
  );
  const maxTabIndex = steps.length - 1;
  const onNext = () => setTabIndex(Math.min(tabIndex + 1, maxTabIndex));
  const onPrev = () => setTabIndex(Math.max(tabIndex - 1, 0));
  return (
    <>
      <Tabs index={tabIndex} display="flex" flexDirection="column" flex={1}>
        <TabList
          as={HStack}
          divider={<Box w="110px" h="2px" backgroundColor={PRIMARY_BLUE} />}
          borderBottomColor="transparent"
          boxShadow="0 1px var(--chakra-colors-gray-100)"
          zIndex={2}
        >
          {steps.map((item, i) => (
            <StepTab
              key={i}
              index={i}
              state={
                tabIndex === i ? 'active' : tabIndex > i ? 'done' : 'pending'
              }
              title={Object(item).props?.title}
            />
          ))}
        </TabList>
        <TabPanels display="flex" flexDirection="column" flex={1}>
          {steps.map((item, i) => (
            <TabPanel
              key={i}
              display="flex"
              flexDirection="column"
              flex={1}
              position="relative"
            >
              <AbsoluteFill>
                <ScrollPane minH="100%" contentContainerProps={{ flexGrow: 1 }}>
                  {tabIndex === i ? item : null}
                </ScrollPane>
              </AbsoluteFill>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <Stack
        align="center"
        backgroundColor="white"
        direction="row"
        justify="right"
        p={4}
        spacing={4}
        boxShadow="0 -1px var(--chakra-colors-gray-100)"
      >
        {tabIndex > 0 && <Button onClick={onPrev}>{t('Previous')}</Button>}
        {tabIndex < maxTabIndex ? (
          <Button onClick={onNext} variant="primary">
            {t('Next')}
          </Button>
        ) : (
          <Button variant="primary" onClick={onSubmit}>
            {submitText ?? t('Submit')}
          </Button>
        )}
      </Stack>
    </>
  );
}

type StepperStepProps = { title: string; children: ReactNode };

export function StepperStep(props: StepperStepProps) {
  return <>{props.children}</>;
}

function StepTab(props: { index: number; title: string; state: StepState }) {
  const { index, state, title } = props;
  const size = 26;
  const borderRadius = size / 2;
  return (
    <Tab style={{ cursor: 'default' }}>
      <HStack>
        <Text
          w={size}
          h={size}
          textAlign="center"
          style={
            state === 'active'
              ? {
                  borderRadius,
                  border: '1px solid black',
                  color: 'white',
                  backgroundColor: PRIMARY_BLUE,
                }
              : state === 'done'
              ? {
                  borderRadius,
                  border: `1px solid ${PRIMARY_BLUE}`,
                  color: PRIMARY_BLUE,
                  backgroundColor: 'white',
                }
              : {
                  borderRadius,
                  border: `1px solid ${DISABLED_GREY}`,
                  color: DISABLED_GREY,
                  backgroundColor: 'white',
                }
          }
        >
          {index + 1}
        </Text>
        <Text>{title}</Text>
      </HStack>
    </Tab>
  );
}
